import React, { useState, useRef, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LifeBuoy, MessageSquare, Sparkles, ChevronRight, Send, Compass } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { id: "technical", label: "My refocus widget isn't appearing" },
  { id: "login", label: "I can't log in" },
  { id: "upgrade", label: "I want to upgrade my plan" },
  { id: "cancel", label: "I want to cancel my subscription" },
  { id: "delete", label: "I need to delete my account" },
  { id: "billing", label: "I have a billing question" },
  { id: "other", label: "Something else" },
];

const RESPONSES = {
  technical: "Let's check three things: 1) Is the OS agent running? (Settings → System tray) 2) Which OS and browser? 3) When did this start? Open a ticket below with that info.",
  login: "Try forgot-password from the login screen. If it's a Google/Microsoft SSO issue, your admin may need to re-approve WYLO in your workspace.",
  upgrade: "Head to Billing → Upgrade plan. You'll see your current usage and a tier comparison. Annual billing is 10% off.",
  cancel: "We'd hate to lose you. Here's a one-month 20% retention offer if you'd like to stay. Otherwise, Billing → Cancel plan starts a 30-day grace period.",
  delete: "We process deletion within 24 hours via our S-Tier team. Settings → Delete my account. You'll get a confirmation email immediately.",
  billing: "Open a ticket below with the invoice number or charge date. A human will respond within a few hours during staffed time.",
  other: "Tell us what's going on. We'll route it to the right person.",
};

export default function Support() {
  const { user } = useAuth();
  const compassEntitled = Boolean(user?.compass_enabled) || user?.role === "s_tier";
  const [category, setCategory] = useState(null);
  const [form, setForm] = useState({ subject: "", message: "" });
  const [open, setOpen] = useState(false);

  const submitTicket = async () => {
    try {
      await api.post("/support/ticket", { category: category, ...form });
      toast.success("Ticket created. We'll be in touch.");
      setOpen(false); setForm({ subject: "", message: "" });
    } catch (e) {
      toast.error("Could not create ticket.");
    }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="support-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold">Support</p>
        <h1 className="font-display text-3xl text-[#1A1A2E] mt-1">We're here to help.</h1>
        <p className="text-sm text-[#555568] mt-2">We typically respond within a few hours during staffed time.</p>
      </div>

      {compassEntitled && <CompassChat user={user} />}

      <section className="rounded-xl border border-[#E8ECF2] bg-white p-6">
        <div className="flex items-center gap-2 mb-4">
          <LifeBuoy className="text-[#1A3A5C]" size={18} />
          <h2 className="font-display text-xl font-bold text-[#1A1A2E]">Diagnostic assistant</h2>
        </div>
        <p className="text-sm text-[#555568] mb-4">Pick the closest match to what's going on. We'll suggest the fastest path.</p>

        <div className="space-y-2">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              onClick={() => setCategory(c.id)}
              className={`w-full text-left px-4 py-3 rounded-md border flex items-center justify-between transition-colors ${
                category === c.id ? "border-[#1A3A5C] bg-[#E8ECF2]" : "border-[#E8ECF2] hover:bg-[#F8F9FC]"
              }`}
              data-testid={`support-cat-${c.id}`}
            >
              <span className="text-sm text-[#1A1A2E]">{c.label}</span>
              <ChevronRight size={14} className="text-[#555568]" />
            </button>
          ))}
        </div>

        {category && (
          <div className="mt-5 rounded-md bg-[#F8F9FC] border border-[#E8ECF2] p-4">
            <p className="text-sm text-[#1A1A2E] leading-relaxed">{RESPONSES[category]}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button onClick={() => setOpen(true)} className="bg-[#2C6BAC] hover:bg-[#1A3A5C] text-white" data-testid="support-open-ticket">
                <MessageSquare size={14} className="mr-2" /> Open a ticket
              </Button>
              <Button variant="outline" onClick={() => setCategory(null)}>Choose another</Button>
            </div>
          </div>
        )}
      </section>

      {open && (
        <section className="rounded-xl border border-[#E8ECF2] bg-white p-6">
          <h3 className="font-display text-lg font-bold text-[#1A1A2E] mb-4">New ticket</h3>
          <div className="space-y-3">
            <div>
              <Label>Subject</Label>
              <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="support-subject" />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="support-message" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submitTicket} className="bg-[#2C6BAC] hover:bg-[#1A3A5C] text-white" data-testid="support-submit-ticket">Send</Button>
            </div>
          </div>
        </section>
      )}

      {!compassEntitled && (
        <section className="rounded-xl bg-gradient-to-br from-[#1A3A5C] to-[#2C6BAC] text-white p-6">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles size={14} />
            <p className="text-xs uppercase tracking-[0.18em] font-semibold">Coming with Compass</p>
          </div>
          <h3 className="font-display text-2xl">An AI-powered support chatbot.</h3>
          <p className="text-sm text-[#E8ECF2]/85 mt-2">
            Compass replaces the rule-based assistant with a Claude-powered chatbot that has context on your workspace.
          </p>
        </section>
      )}
    </div>
  );
}

function CompassChat({ user }) {
  const [messages, setMessages] = useState([
    { role: "assistant", content: `Hi ${user?.name?.split(" ")[0] || "there"} - I'm Compass. Ask me about your focus data, FACE events, hierarchy strategy, or how to get more out of WYLO.` },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, busy]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/compass/chat", { messages: next });
      setMessages((cur) => [...cur, { role: "assistant", content: data.reply }]);
    } catch (e) {
      const detail = e.response?.data?.detail || "Compass is unavailable right now.";
      setMessages((cur) => [...cur, { role: "assistant", content: detail }]);
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-xl border border-[#1A3A5C]/20 bg-white p-6" data-testid="compass-chat">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Compass className="text-[#1A3A5C]" size={18} />
          <h2 className="font-display text-xl font-bold text-[#1A1A2E]">WYLO Compass</h2>
        </div>
        <span className="text-[10px] uppercase tracking-[0.2em] text-[#1D9E75] font-semibold">Live · Claude Sonnet 4.5</span>
      </div>

      <div
        ref={scrollerRef}
        className="rounded-md border border-[#E8ECF2] bg-[#F8F9FC] p-4 max-h-96 overflow-y-auto space-y-3"
        data-testid="compass-chat-scroller"
      >
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[80%] rounded-lg px-3 py-2 text-sm leading-relaxed ${
                m.role === "user"
                  ? "bg-[#1A3A5C] text-white"
                  : "bg-white border border-[#E8ECF2] text-[#1A1A2E]"
              }`}
              data-testid={`compass-msg-${m.role}-${i}`}
            >
              {m.content}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start">
            <div className="bg-white border border-[#E8ECF2] rounded-lg px-3 py-2 text-xs text-[#555568] italic">
              Compass is thinking…
            </div>
          </div>
        )}
      </div>

      <div className="mt-3 flex gap-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Ask Compass…"
          disabled={busy}
          data-testid="compass-chat-input"
        />
        <Button
          onClick={send}
          disabled={busy || !input.trim()}
          className="bg-[#1A3A5C] hover:bg-[#142E48] text-white"
          data-testid="compass-chat-send"
        >
          <Send size={14} />
        </Button>
      </div>
      <p className="text-xs text-[#555568] mt-2">
        Compass uses your aggregate focus data - no keystroke content, no screen content, never personally identifying.
      </p>
    </section>
  );
}
